import { useState } from 'react';

export function MicPermissionModal({ onGranted, onDismiss }: { onGranted: () => void; onDismiss: () => void }) {
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestPermission = async () => {
    setRequesting(true);
    setError(null);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      onGranted();
    } catch (err: unknown) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Permission denied. Please allow microphone access in your browser/system settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone and try again.');
        } else {
          setError(`Failed to access microphone: ${err.message}`);
        }
      } else {
        setError('Failed to access microphone');
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#1a1a2e] border border-purple-500/30 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-white font-bold text-xl">Microphone Access Required</h2>
            <p className="text-gray-400 text-sm">For Push-to-Talk to work</p>
          </div>
        </div>

        <p className="text-gray-300 text-sm mb-6 leading-relaxed">
          Stream Buddy needs access to your microphone so you can talk to your AI companion using Push-to-Talk.
          Your audio is only sent when you hold the PTT key.
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-sm font-medium transition-colors"
          >
            Skip for Now
          </button>
          <button
            type="button"
            onClick={() => { void requestPermission(); }}
            disabled={requesting}
            className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-bold transition-all disabled:opacity-50"
          >
            {requesting ? 'Requesting...' : 'Enable Microphone'}
          </button>
        </div>
      </div>
    </div>
  );
}
